import React, { useState } from "react";
import axios from "axios";
import { Plus, Loader2, Pencil, Trash2, Receipt, X, Download } from "lucide-react";
import { downloadCsv } from "./TransactionsReport";
import { API, inputCls, btnPrimary, btnGhost, Field, Empty, money, TX_METHODS, TX_TYPES, TX_STATUSES } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: today(), amount: "", currency: "USD", method: "tarjeta", tx_type: "venta", reference: "", status: "aprobada", note: "" });

const TxForm = ({ initial, onCancel, onSaved, appId, headers }) => {
  const [form, setForm] = useState(initial || blank());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      const res = initial?.id
        ? await axios.put(`${API}/crm/transactions/${initial.id}`, payload, { headers })
        : await axios.post(`${API}/crm/clients/${appId}/transactions`, payload, { headers });
      onSaved(res.data, Boolean(initial?.id));
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Revisa los campos: fecha, monto (> 0), método y tipo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="tx-form" className="bg-white border-2 border-[#0fe814]/40 rounded-2xl p-4 sm:p-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Fecha"><input data-testid="tx-date" type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
        <Field label="Monto (USD)"><input data-testid="tx-amount" type="number" min="0" step="0.01" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" /></Field>
        <Field label="Método">
          <select data-testid="tx-method" className={inputCls} value={form.method} onChange={(e) => set("method", e.target.value)}>
            {Object.entries(TX_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Tipo">
          <select data-testid="tx-type" className={inputCls} value={form.tx_type} onChange={(e) => set("tx_type", e.target.value)}>
            {Object.entries(TX_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Referencia"><input data-testid="tx-reference" className={inputCls} value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Ej: AUT-123456" /></Field>
        <Field label="Estado">
          <select data-testid="tx-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
            {Object.entries(TX_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Nota" className="sm:col-span-2"><input data-testid="tx-note" className={inputCls} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Opcional" /></Field>
      </div>
      {error && <p className="mt-3 text-[12px] font-bold text-[#ff603e]">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}><X size={13} /> Cancelar</button>
        <button data-testid="tx-save" className={btnPrimary} onClick={submit} disabled={saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {initial?.id ? "Guardar cambios" : "Registrar"}</button>
      </div>
    </div>
  );
};

const STATUS_CLS = { aprobada: "bg-[#0fe814]/15 text-[#0fb013]", pendiente: "bg-amber-100 text-amber-700", rechazada: "bg-[#ff603e]/10 text-[#ff603e]" };
const TYPE_SIGN = { venta: "+", reembolso: "−", cashout: "↗", comision: "−" };

const ClientTransactions = ({ appId, headers, canManage, transactions, totals, onChange }) => {
  const [mode, setMode] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const remove = async (tx) => {
    if (!window.confirm(`¿Eliminar la transacción ${TX_TYPES[tx.tx_type]} ${money(tx.amount)}?`)) return;
    setDeleting(tx.id);
    try {
      await axios.delete(`${API}/crm/transactions/${tx.id}`, { headers });
      onChange();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="crm-transactions">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["Ventas", totals.ventas, "text-[#0fb013]"], ["Reembolsos", totals.reembolsos, "text-[#ff603e]"], ["Comisiones", totals.comisiones, "text-black/70"],
          ["Cashouts", totals.cashouts, "text-blue-600"], ["Neto", totals.neto, "text-black"],
        ].map(([l, v, c]) => (
          <div key={l} className="bg-white border border-black/[0.06] rounded-2xl p-3.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-black/40">{l}</p>
            <p data-testid={`tx-total-${l.toLowerCase()}`} className={`mt-1 font-extrabold text-[18px] ${c}`}>{money(v)}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-black/55">{transactions.length} transacción(es) · totales sobre aprobadas</p>
        <div className="flex items-center gap-2">
          <button data-testid="tx-export-csv" className={btnGhost} disabled={transactions.length === 0} onClick={() => downloadCsv({ app_id: appId }, headers)}><Download size={13} /> Exportar CSV</button>
          {canManage && !mode && <button data-testid="tx-new" className={btnPrimary} onClick={() => setMode({})}><Plus size={13} /> Nueva transacción</button>}
        </div>
      </div>

      {mode && (
        <TxForm appId={appId} headers={headers} initial={mode.id ? mode : null} onCancel={() => setMode(null)}
          onSaved={() => { setMode(null); onChange(); }} />
      )}

      {transactions.length === 0 ? <Empty icon={Receipt} text="Sin transacciones registradas" /> : (
        <div className="bg-white border border-black/[0.06] rounded-2xl overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-[#fafcf9] text-[10px] uppercase tracking-wider text-black/45">
              <tr>
                <th className="text-left px-4 py-2.5 font-bold">Fecha</th><th className="text-left px-4 py-2.5 font-bold">Tipo</th><th className="text-left px-4 py-2.5 font-bold">Método</th>
                <th className="text-left px-4 py-2.5 font-bold hidden md:table-cell">Referencia</th><th className="text-right px-4 py-2.5 font-bold">Monto</th>
                <th className="text-left px-4 py-2.5 font-bold">Estado</th>{canManage && <th className="px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} data-testid={`tx-row-${tx.id}`} className="border-t border-black/[0.05] hover:bg-black/[0.015]">
                  <td className="px-4 py-3 font-semibold text-black/70 whitespace-nowrap">{tx.date}</td>
                  <td className="px-4 py-3 font-bold text-black">{TX_TYPES[tx.tx_type]}</td>
                  <td className="px-4 py-3 font-semibold text-black/70">{TX_METHODS[tx.method]}</td>
                  <td className="px-4 py-3 font-mono text-black/60 hidden md:table-cell">{tx.reference || "—"}{tx.note ? <span className="block text-[11px] font-sans text-black/40 truncate max-w-[220px]">{tx.note}</span> : null}</td>
                  <td className="px-4 py-3 text-right font-extrabold text-black whitespace-nowrap">{TYPE_SIGN[tx.tx_type]} {money(tx.amount)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_CLS[tx.status]}`}>{TX_STATUSES[tx.status]}</span></td>
                  {canManage && (
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button data-testid={`tx-edit-${tx.id}`} title="Editar" className="p-1.5 rounded-lg hover:bg-black/[0.06]" onClick={() => setMode({ ...tx, amount: String(tx.amount) })}><Pencil size={13} /></button>
                      <button data-testid={`tx-delete-${tx.id}`} title="Eliminar" className="p-1.5 rounded-lg hover:bg-[#ff603e]/10 text-[#ff603e]" onClick={() => remove(tx)} disabled={deleting === tx.id}>
                        {deleting === tx.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientTransactions;
